import axios from 'axios';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry } from '@shared/utils/with-antibot-retry';

const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

/**
 * Builds auth + anti-bot headers for portal-integration CRM reply calls.
 *
 * @returns Headers map for axios (always includes X-Anti-Bot).
 */
function buildHeaders(opts?: { reload?: boolean }): Promise<Record<string, string>> {
  return buildPortalIntegrationHeaders('portal_social_message', opts);
}

export type SocialMessagePlatform = 'facebook' | 'instagram';

export type SendSocialMessageResult = {
  status: 'sent';
  message_id: string;
  page_name?: string;
  page_id?: string;
};

/**
 * Lifecycle event for one outbound reply. `start` fires synchronously when
 * the user hits send -- before the request resolves -- so the caller can
 * render the message optimistically (with a pending indicator) instead of
 * blocking the composer until the Graph API call completes.
 */
export type SocialSendEvent =
  | { phase: 'start'; localId: string; text: string; platform: SocialMessagePlatform }
  | { phase: 'success'; localId: string }
  | { phase: 'error'; localId: string };

export type PortalConnectionInfo = {
  fb_connected?: boolean;
  fb_page_name?: string | null;
  fb_page_id?: string | null;
  ig_account_id?: string | null;
  ig_account_name?: string | null;
  /** Native Instagram Business Login connection -- no Facebook Page involved. */
  ig_native_connected?: boolean;
  ig_native_username?: string | null;
};

/**
 * Loads portal settings needed to show the connected Page / IG asset.
 *
 * @param agencyId - Agency identifier.
 * @returns Connection info from portal settings.
 */
export async function fetchPortalConnection(agencyId: string): Promise<PortalConnectionInfo> {
  const res = await withAntiBotRetry(
    (headers) =>
      axios.get(`${API_BASE}/portal-integration/agencies/${agencyId}/settings`, { headers }),
    buildHeaders,
  );
  return (res.data ?? {}) as PortalConnectionInfo;
}

/**
 * Sends an outbound Messenger or Instagram Direct reply via Housien.
 *
 * @param agencyId - Agency identifier.
 * @param platform - facebook or instagram.
 * @param recipientId - PSID or IGSID.
 * @param text - Reply body.
 * @returns Graph send result including page_name for UI confirmation.
 */
export async function sendSocialMessage(
  agencyId: string,
  platform: SocialMessagePlatform,
  recipientId: string,
  text: string,
): Promise<SendSocialMessageResult> {
  const res = await withAntiBotRetry(
    (headers) =>
      axios.post(
        `${API_BASE}/portal-integration/agencies/${agencyId}/social-messages`,
        { platform, recipient_id: recipientId, text },
        { headers },
      ),
    buildHeaders,
  );
  return res.data as SendSocialMessageResult;
}

/**
 * Extracts the backend's `reason` code from a failed sendSocialMessage call,
 * e.g. "messaging_window_expired" when Meta's 24h standard messaging window
 * has passed. Falls back to "" when the error has no such body (network
 * failure, anti-bot challenge, etc.) so callers can show a generic message.
 */
export function socialMessageErrorReason(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const response = (error as { response?: { data?: { reason?: unknown } } }).response;
  const reason = response?.data?.reason;
  return typeof reason === 'string' ? reason : '';
}
